/**
 * Reporting-week calendar (plan §3.1, §6).
 *
 * - A reporting week is keyed by a **Sunday** and covers the seven days ending the Saturday before it
 *   (the column dated Sun 11 Oct covers Sun 4 – Sat 10 Oct).
 * - The **current week** is the Sunday on or before *today in Asia/Amman* — never the last row of a table
 *   and never the last week that happens to have data.
 * - Weeks belong to the calendar year of their Sunday and are numbered 1..52/53 in the order their Sundays
 *   fall in that year.
 *
 * Dates are plain ISO calendar dates (`YYYY-MM-DD`), the shape of the `weeks.week_date` column. All arithmetic
 * runs on UTC midnights so no host time zone or daylight-saving rule can move a date.
 */
/** A business rule, not a deployment setting — deliberately a constant, never environment. */
export const REPORTING_TIMEZONE = 'Asia/Amman';
const DAY_MS = 86_400_000;
const SUNDAY = 0;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (value, length) => String(value).padStart(length, '0');
const fromUtcMs = (ms) => {
    const date = new Date(ms);
    return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1, 2)}-${pad(date.getUTCDate(), 2)}`;
};
/** True for a real calendar date in `YYYY-MM-DD` form (rejects `2026-02-30`, `2026-2-3`, …). */
export const isIsoDate = (value) => {
    if (typeof value !== 'string') {
        return false;
    }
    const match = ISO_DATE_PATTERN.exec(value);
    if (!match) {
        return false;
    }
    const [, year, month, day] = match.map(Number);
    // `setUTCFullYear`, not `Date.UTC`: the latter maps years 0-99 onto 1900-1999.
    const date = new Date(0);
    date.setUTCFullYear(year, month - 1, day);
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};
const toUtcMs = (date) => {
    if (!isIsoDate(date)) {
        throw new RangeError(`Not an ISO calendar date: ${String(date)}`);
    }
    return Date.parse(`${date}T00:00:00Z`);
};
export const addDays = (date, days) => fromUtcMs(toUtcMs(date) + days * DAY_MS);
/** 0 = Sunday … 6 = Saturday. */
export const dayOfWeek = (date) => new Date(toUtcMs(date)).getUTCDay();
export const isSunday = (date) => dayOfWeek(date) === SUNDAY;
export const sundayOnOrBefore = (date) => addDays(date, -dayOfWeek(date));
const yearOf = (date) => Number(date.slice(0, 4));
/** 1-based day of the calendar year. */
const dayOfYear = (date) => (toUtcMs(date) - toUtcMs(`${date.slice(0, 4)}-01-01`)) / DAY_MS + 1;
const requireSunday = (date) => {
    if (!isSunday(date)) {
        throw new RangeError(`Reporting weeks are keyed by a Sunday; ${date} is not one`);
    }
};
/** The calendar date it is *in Amman* at the instant `now`. */
export const reportingDateOf = (now) => {
    if (Number.isNaN(now.getTime())) {
        throw new RangeError('Invalid instant');
    }
    // `en-CA` formats as YYYY-MM-DD; the explicit parts keep the result independent of locale data quirks.
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: REPORTING_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const part = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    return `${part.year}-${part.month}-${part.day}`;
};
/** The Sunday on or before today in Asia/Amman. */
export const currentReportingSunday = (now) => sundayOnOrBefore(reportingDateOf(now));
/** `W41 · 11 Oct 2026`. */
export const formatWeekLabel = (weekDate) => {
    requireSunday(weekDate);
    const [year, month, day] = weekDate.split('-').map(Number);
    return `W${pad(weekNumberOf(weekDate), 2)} · ${day} ${MONTH_NAMES[month - 1]} ${year}`;
};
/** Position of a Sunday among the Sundays of its own calendar year (1-based). */
export const weekNumberOf = (weekDate) => {
    requireSunday(weekDate);
    return Math.floor((dayOfYear(weekDate) - 1) / 7) + 1;
};
export const toReportingWeek = (weekDate) => ({
    year: yearOf(weekDate),
    weekNumber: weekNumberOf(weekDate),
    weekDate,
    label: formatWeekLabel(weekDate),
});
/** Every Sunday of `year`, in order (52 or 53 of them). */
export const sundaysInYear = (year) => {
    if (!Number.isInteger(year) || year < 1 || year > 9999) {
        throw new RangeError(`Not a calendar year: ${year}`);
    }
    const sundays = [];
    const januaryFirst = `${pad(year, 4)}-01-01`;
    for (let sunday = addDays(januaryFirst, (7 - dayOfWeek(januaryFirst)) % 7); yearOf(sunday) === year; sunday = addDays(sunday, 7)) {
        sundays.push(sunday);
    }
    return sundays;
};
/** The reporting weeks of `year`, ready to be inserted into `weeks`. */
export const reportingWeeksOfYear = (year) => sundaysInYear(year).map(toReportingWeek);
/** The week "Add reporting week" appends after the last one: the following Sunday. */
export const nextWeekAfter = (lastSunday) => {
    requireSunday(lastSunday);
    return toReportingWeek(addDays(lastSunday, 7));
};
/** The seven days a reporting Sunday reports on. */
export const coveredWindow = (weekDate) => {
    requireSunday(weekDate);
    return { from: addDays(weekDate, -7), to: addDays(weekDate, -1) };
};
/**
 * The week whose Sunday is the current reporting Sunday, or `null` when that week has not been generated.
 * Deliberately *not* "the latest week" or "the latest week with data".
 */
export const findCurrentWeek = (weeks, now) => {
    const sunday = currentReportingSunday(now);
    return weeks.find((week) => week.weekDate === sunday) ?? null;
};
//# sourceMappingURL=week-calendar.js.map